import React from 'react';
import type { HMIComponent } from '@/hmi/model/types';
import { useSimulationStore } from '@/store/simulationStore';
import { useUiStore } from '@/store/uiStore';
import { parseAddress } from '@/plc/address/parser';
import { useHMILiveValue } from './hooks/useHMILiveValue';
import { FanSvg } from '@/ui/scene/components/FanSvg';
import { AcMotor3PhaseSvg } from '@/ui/scene/components/AcMotor3PhaseSvg';

interface Props {
  component: HMIComponent;
  isRuntime?: boolean;
}

export function HMIComponentRenderer({ component, isRuntime = false }: Props) {
  const engine = useSimulationStore((s) => s.engine);
  const simState = useSimulationStore((s) => s.snapshot.state);
  const activeMode = useUiStore((s) => s.activeMode);
  const liveValue = useHMILiveValue(component.binding);
  const [isPressedLocally, setIsPressedLocally] = React.useState(false);

  // Data binding logic: live when explicitly in runtime window, or when simulation is running
  const isSimActive = simState === 'RUNNING' || simState === 'PAUSED';
  const isLive = isRuntime || isSimActive || (activeMode !== 'design' && activeMode !== 'hmi');
  const value = isLive ? liveValue : null;

  // Common styles
  const baseStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: (component.props.color as string) || '#cccccc',
    border: '1px solid #333',
    boxSizing: 'border-box',
    overflow: 'hidden',
    position: 'relative',
    userSelect: 'none',
  };

  const handlePointerDown = () => {
    if (!isLive) return;
    setIsPressedLocally(true);
    if (component.type === 'PushButton' && component.binding.type === 'PLC') {
      const behavior = (component.props.behavior as string) || 'momentary';
      try {
        const addr = parseAddress(component.binding.address);
        if (addr && addr.size === 'X') {
          if (behavior === 'toggle') {
            const area = addr.area === 'I' ? engine.host.pi.inputs : addr.area === 'Q' ? engine.host.pi.outputs : engine.host.pi.memory;
            const currentVal = area.getBit(addr.byteOffset, addr.bitOffset);
            const newVal = !currentVal;
            if (addr.area === 'I') {
              useSimulationStore.getState().setInput(addr.byteOffset, addr.bitOffset, newVal);
              engine.host.pi.inputForces.setForce(addr.byteOffset, addr.bitOffset, newVal);
            } else {
              area.setBit(addr.byteOffset, addr.bitOffset, newVal);
            }
          } else {
            // Momentary push
            if (addr.area === 'I') {
              useSimulationStore.getState().setInput(addr.byteOffset, addr.bitOffset, true);
              engine.host.pi.inputForces.setForce(addr.byteOffset, addr.bitOffset, true);
            } else {
              const area = addr.area === 'Q' ? engine.host.pi.outputs : engine.host.pi.memory;
              area.setBit(addr.byteOffset, addr.bitOffset, true);
            }
          }
        }
      } catch {
        // Error
      }
    }
  };

  const handlePointerUp = () => {
    setIsPressedLocally(false);
    if (!isLive) return;
    if (component.type === 'PushButton' && component.binding.type === 'PLC') {
      const behavior = (component.props.behavior as string) || 'momentary';
      if (behavior === 'toggle') return; // Do nothing on release for toggle

      try {
        const addr = parseAddress(component.binding.address);
        if (addr && addr.size === 'X') {
          if (addr.area === 'I') {
            useSimulationStore.getState().setInput(addr.byteOffset, addr.bitOffset, false);
            engine.host.pi.inputForces.clearForce(addr.byteOffset, addr.bitOffset);
          } else {
            const area = addr.area === 'Q' ? engine.host.pi.outputs : engine.host.pi.memory;
            area.setBit(addr.byteOffset, addr.bitOffset, false);
          }
        }
      } catch {
        // Error
      }
    }
  };

  switch (component.type) {
    case 'Text':
      return (
        <div style={{ ...baseStyle, border: 'none', background: 'transparent', color: (component.props.color as string) || '#ffffff', fontWeight: 600 }}>
          {(component.props.label as string) || 'Text'}
        </div>
      );

    case 'PushButton': {
      const isPressed = isPressedLocally || Boolean(value);
      return (
        <button
          style={{
            ...baseStyle,
            cursor: isLive ? 'pointer' : 'default',
            background: isPressed ? 'var(--color-primary)' : ((component.props.color as string) || '#2563eb'),
            color: '#ffffff',
            fontWeight: 600,
            borderRadius: 4,
            boxShadow: isPressed ? 'inset 0 2px 4px rgba(0,0,0,0.5)' : '0 2px 4px rgba(0,0,0,0.25)',
            transform: isPressed ? 'translateY(1px)' : 'none',
            transition: 'all 0.05s ease',
          }}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          {(component.props.label as string) || 'Button'}
        </button>
      );
    }
      
    case 'PilotLamp':
      return (
        <div style={{ ...baseStyle, borderRadius: '50%', background: value ? ((component.props.color as string) || 'red') : '#333' }}>
          {Boolean(component.props.label) && <span style={{ position: 'absolute', bottom: -20, color: '#fff', fontSize: '10px' }}>{String(component.props.label)}</span>}
        </div>
      );
      
    case 'NumericDisplay':
      return (
        <div style={{ ...baseStyle, background: '#000', color: '#0f0', fontFamily: 'monospace', fontSize: '1.2rem' }}>
          {typeof value === 'number' ? value.toFixed(1) : '0.0'}
        </div>
      );
      
    case 'LinearGauge': {
      const min = component.binding.scaleMin || 0;
      const max = component.binding.scaleMax || 100;
      const v = typeof value === 'number' ? value : 0;
      const percent = Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100));
      return (
        <div style={{ ...baseStyle, background: '#222', flexDirection: 'column-reverse', justifyContent: 'flex-start' }}>
          <div style={{ width: '100%', height: `${percent}%`, background: (component.props.color as string) || 'cyan', transition: 'height 0.1s' }} />
          <span style={{ position: 'absolute', top: 4, color: '#fff', fontSize: '10px' }}>{v.toFixed(1)}</span>
        </div>
      );
    }
      
    case 'TankLevel':
    case 'BarGraph': {
      const bMin = component.binding.scaleMin || 0;
      const bMax = component.binding.scaleMax || 100;
      const bV = typeof value === 'number' ? value : 0;
      const bPercent = Math.max(0, Math.min(100, ((bV - bMin) / (bMax - bMin)) * 100));
      return (
        <div style={{ ...baseStyle, background: '#222', flexDirection: 'column-reverse', justifyContent: 'flex-start', borderRadius: component.type === 'TankLevel' ? '4px 4px 16px 16px' : '0' }}>
          <div style={{ width: '100%', height: `${bPercent}%`, background: (component.props.color as string) || 'cyan', transition: 'height 0.2s' }} />
          <span style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', color: '#fff' }}>{bV.toFixed(0)}</span>
        </div>
      );
    }
      
    case 'AlarmBanner':
      return (
        <div style={{ ...baseStyle, background: value ? 'red' : '#333', color: '#fff', fontWeight: 'bold' }}>
          {value ? String(component.props.label || 'ALARM ACTIVE') : 'NO ALARMS'}
        </div>
      );

    case 'CoolingFan': {
      const isFanRunning = Boolean(value);
      return (
        <div style={{ ...baseStyle, background: 'transparent', border: 'none', padding: 2 }}>
          <FanSvg
            isRunning={isFanRunning}
            color={(component.props.color as string) || '#187AA0'}
            width="100%"
            height="100%"
            label={(component.props.label as string) || undefined}
          />
        </div>
      );
    }

    case 'MotorStatus': {
      const isMotorRunning = Boolean(value);
      const isFan = component.props.variant === 'fan';
      return (
        <div style={{ ...baseStyle, background: 'transparent', border: 'none', padding: 2 }}>
          {isFan ? (
            <FanSvg
              isRunning={isMotorRunning}
              color={(component.props.color as string) || '#187AA0'}
              width="100%"
              height="100%"
              label={(component.props.label as string) || undefined}
            />
          ) : (
            <AcMotor3PhaseSvg
              isRunning={isMotorRunning}
              color={(component.props.color as string) || '#146E46'}
              width="100%"
              height="100%"
              label={(component.props.label as string) || undefined}
            />
          )}
        </div>
      );
    }

    default:
      return (
        <div style={baseStyle}>
          {String(component.props.label || component.type)}
        </div>
      );
  }
}
